import { push } from 'redux-first-history';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import getDocumentStatusForUser from '@app/libs/getDocumentStatusForUser';
import workspaceService from '@app/libs/workspaceService';
import { REQUESTER_LINK__FETCH_REQUEST } from '@app/store/actionTypes';
import Document from '@app/store/document/Document';
import Requester from '@app/store/documents/Requester';
import DocumentsStatus from '@app/store/documents/Status';
import { modalOpen } from '@app/store/modal/actions';
import {
  fetchRequesterLink,
  fetchRequesterLinkFailure,
  fetchRequesterLinkSuccess,
} from '@app/store/requesterLink/actions';
import { getDocumentsByIdDeprecated } from '@app/store/transport-layer/document/documents';
import { getUserEmail } from '@app/store/user/selectors';

export function* redirectToDocument({
  documentStatus,
  state,
}: {
  documentStatus: DocumentsStatus;
  state: {
    contactInfo: string | null;
    documentId: string;
    exitPath?: string;
    participantId: string | null;
    requester: Requester;
  };
}) {
  // eslint-disable-next-line default-case
  switch (documentStatus) {
    case DocumentsStatus.COMPLETED:
      yield put(push('/document/completed', state));
      break;
    case DocumentsStatus.ON_PROCESSING:
      yield put(modalOpen('PARTICIPANT_PROCESSING_MODAL', state));
      break;
    case DocumentsStatus.PROCESSING_FAILED:
      yield put(modalOpen('PARTICIPANT_PROCESSING_FAILED_MODAL'));
      break;
    case DocumentsStatus.NEED_SIGNING:
      yield put(push('/signing/authentication', state));
      break;
    case DocumentsStatus.WAIT_FOR_SIGNING:
      yield put(push('/document/wait-for-signing', state));
      break;
    case DocumentsStatus.ON_SIGNING:
      yield put(push('/document/on-signing', state));
      break;
    case DocumentsStatus.REQUEST_CANCELLED:
    case DocumentsStatus.SIGNING_CANCELLED:
    case DocumentsStatus.REJECTED:
      yield put(push('/document/aborted', state));
      break;
    case DocumentsStatus.SCHEDULE_READY:
    case DocumentsStatus.SCHEDULE_CANCELED:
    case DocumentsStatus.SCHEDULE_START_FAILED:
      yield put(push('/document/scheduled', state));
      break;
  }
}

export function* fetchRequesterLinkSaga({
  payload: { documentId, exitPath },
}: ReturnType<typeof fetchRequesterLink>) {
  try {
    const document: Document = yield call(getDocumentsByIdDeprecated, {
      workspaceId: workspaceService.getWorkspaceId(),
      documentId,
    });
    const userEmail: ReturnType<typeof getUserEmail> = yield select(getUserEmail);
    const participant = document.participants.find(
      (participant) => !!userEmail && participant.signingMethod.contactInfo === userEmail
    );
    const state = {
      contactInfo: participant ? participant.signingMethod.contactInfo : null,
      exitPath,
      documentId,
      participantId: participant ? participant.participantId : null,
      requester: document.requester,
    };

    if (userEmail) {
      // userEmail 있는 경우 내문서 문서 상태 분류방법을 따릅니다.
      const documentStatus = getDocumentStatusForUser(document, userEmail);
      yield call(redirectToDocument, { documentStatus, state });
    } else {
      // userEmail이 없는 경우 로그인페이지로 보냅니다.
      yield put(push('/signing/authentication', state));
    }
    yield put(fetchRequesterLinkSuccess());
  } catch (error) {
    yield put(fetchRequesterLinkFailure(error));
    if (error.message === 'document not found') {
      yield put(push('/authentication/signin'));
      yield put(
        modalOpen('CONFIRM', {
          title: '안내',
          content: '문서를 찾을 수 없습니다.',
        })
      );
    } else {
      yield put(
        modalOpen('CONFIRM', {
          content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
        })
      );
    }
  }
}

export default function* saga() {
  yield all([takeEvery(REQUESTER_LINK__FETCH_REQUEST, fetchRequesterLinkSaga)]);
}
